// src/pages/EmployeesPage.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, where, setDoc, getDoc } from "firebase/firestore";
import '../css/employees.css';
import defaultAvatar from '../assets/default.jpg';

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddDepartmentModalOpen, setIsAddDepartmentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isVacationSettingsModalOpen, setIsVacationSettingsModalOpen] = useState(false);
  const [vacationSettingsEmployee, setVacationSettingsEmployee] = useState(null);
  const [yearlyVacationDefaults, setYearlyVacationDefaults] = useState({});
  const [additionalVacationDays, setAdditionalVacationDays] = useState({});
  const [newEmployee, setNewEmployee] = useState({
    firstName: '',
    lastName: '',
    department: '',
    vacationDays: 20,
    sickDays: 0,
    imageUrl: ''
  });
  const [newDepartment, setNewDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([2025, 2026, 2027]);

  useEffect(() => {
    fetchData();
    fetchAvailableYears();
  }, []);

  const fetchAvailableYears = async () => {
    try {
      // Dohvati sve zaposlenike
      const employeesSnapshot = await getDocs(collection(db, 'employees'));
      const yearsSet = new Set([2025, 2026, 2027]); // Default godine
      
      // Za svakog zaposlenika provjeri koje godine postoje u yearlyCounters
      for (const empDoc of employeesSnapshot.docs) {
        const countersSnapshot = await getDocs(collection(db, `employees/${empDoc.id}/yearlyCounters`));
        countersSnapshot.docs.forEach(counterDoc => {
          const year = parseInt(counterDoc.id);
          if (!isNaN(year)) {
            yearsSet.add(year);
          }
        });
      }
      
      // Sortiraj godine silazno
      const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
      setAvailableYears(sortedYears);
      
      // Ako trenutno odabrana godina nije u listi, postavi na prvu dostupnu
      if (sortedYears.length > 0 && !sortedYears.includes(selectedYear)) {
        setSelectedYear(sortedYears[0]);
      }
    } catch (error) {
      console.error('Greška pri dohvaćanju dostupnih godina:', error);
      // Fallback na default godine
      setAvailableYears([2025, 2026, 2027]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesSnapshot, departmentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'employees')),
        getDocs(collection(db, 'departments'))
      ]);

      const employeesData = await Promise.all(employeesSnapshot.docs.map(async doc => {
        const employeeData = doc.data();
        
        // Dohvati sve countere
        const countersSnapshot = await getDocs(collection(db, `employees/${doc.id}/yearlyCounters`));
        const yearlyCounters = {};
        
        countersSnapshot.docs.forEach(counterDoc => {
          yearlyCounters[counterDoc.id] = counterDoc.data();
        });

        return {
          id: doc.id,
          ...employeeData,
          yearlyCounters
        };
      }));

      const departmentsData = departmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));

      setEmployees(employeesData);
      setDepartments(departmentsData);
    } catch (error) {
      console.error('Greška pri dohvaćanju podataka:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'employees'), {
        ...newEmployee,
        createdAt: new Date().toISOString()
      });
      setIsAddModalOpen(false);
      setNewEmployee({
        firstName: '',
        lastName: '',
        department: '',
        vacationDays: 20,
        sickDays: 0,
        imageUrl: ''
      });
      fetchData();
    } catch (error) {
      console.error('Greška pri dodavanju zaposlenika:', error);
    }
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    if (!newDepartment.trim()) return;

    try {
      await addDoc(collection(db, 'departments'), {
        name: newDepartment,
        createdAt: new Date().toISOString()
      });
      setIsAddDepartmentModalOpen(false);
      setNewDepartment('');
      fetchData();
    } catch (error) {
      console.error('Greška pri dodavanju odjela:', error);
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (!window.confirm('Jeste li sigurni da želite izbrisati ovaj odjel?')) return;

    try {
      await deleteDoc(doc(db, 'departments', departmentId));
      fetchData();
    } catch (error) {
      console.error('Greška pri brisanju odjela:', error);
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, 'employees', editingEmployee.id), {
        ...editingEmployee,
        updatedAt: new Date().toISOString()
      });
      setIsEditModalOpen(false);
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      console.error('Greška pri ažuriranju zaposlenika:', error);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Jeste li sigurni da želite obrisati ovog zaposlenika? Svi njegovi eventi će također biti obrisani.')) {
      try {
        // Prvo brišemo sve evente zaposlenika
        const eventsQuery = query(collection(db, "events"), where("employeeId", "==", employeeId));
        const eventsSnapshot = await getDocs(eventsQuery);
        const deletePromises = eventsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        // Brišemo sve godišnje countere zaposlenika
        const countersSnapshot = await getDocs(collection(db, `employees/${employeeId}/yearlyCounters`));
        const counterDeletePromises = countersSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(counterDeletePromises);

        // Na kraju brišemo zaposlenika
        await deleteDoc(doc(db, "employees", employeeId));
        
        // Osvježavamo prikaz
        fetchData();
      } catch (error) {
        console.error("Greška pri brisanju zaposlenika:", error);
        alert("Došlo je do greške pri brisanju zaposlenika.");
      }
    }
  };

  const handleOpenVacationSettings = (employee) => {
    setVacationSettingsEmployee(employee);
    // Inicijaliziraj yearlyVacationDefaults s postojećim vrijednostima
    const defaults = {};
    const additional = {};
    
    // Dodaj godine od 2025 do 2027
    for (let year = 2025; year <= 2027; year++) {
      const yearCounter = employee.yearlyCounters?.[year];
      defaults[year] = yearCounter?.defaultVacationDays ?? employee.vacationDays ?? 20;
      additional[year] = 0; // Inicijaliziraj dodatne dane na 0
    }
    
    setYearlyVacationDefaults(defaults);
    setAdditionalVacationDays(additional);
    setIsVacationSettingsModalOpen(true);
  };

  const handleSaveVacationSettings = async () => {
    if (!vacationSettingsEmployee) return;

    try {
      // Spremi defaultne vrijednosti za svaku godinu u yearlyCounters
      for (const [year, defaultDays] of Object.entries(yearlyVacationDefaults)) {
        const counterRef = doc(db, `employees/${vacationSettingsEmployee.id}/yearlyCounters/${year}`);
        const counterDoc = await getDoc(counterRef);
        
        const currentCounter = counterDoc.exists() ? counterDoc.data() : {
          sickDays: 0,
          workFromHomeDays: 0,
          year: parseInt(year),
          vacationDaysLeft: parseInt(defaultDays)
        };

        // Dodaj dodatne dane na trenutni vacationDaysLeft
        const additionalDays = parseInt(additionalVacationDays[year] || 0);
        const currentVacationDays = currentCounter.vacationDaysLeft !== undefined 
          ? currentCounter.vacationDaysLeft 
          : parseInt(defaultDays);
        const newVacationDaysLeft = currentVacationDays + additionalDays;

        // Ažuriraj counter
        await setDoc(counterRef, {
          ...currentCounter,
          defaultVacationDays: parseInt(defaultDays),
          vacationDaysLeft: newVacationDaysLeft
        }, { merge: true });
      }

      setIsVacationSettingsModalOpen(false);
      setVacationSettingsEmployee(null);
      setYearlyVacationDefaults({});
      setAdditionalVacationDays({});
      await fetchData();
      await fetchAvailableYears(); // Osvježi dostupne godine
    } catch (error) {
      console.error('Greška pri spremanju postavki godišnjeg:', error);
      alert('Došlo je do greške pri spremanju postavki.');
    }
  };

  const handleAddYear = () => {
    const currentYear = new Date().getFullYear();
    const maxYear = Math.max(...Object.keys(yearlyVacationDefaults).map(y => parseInt(y)), currentYear);
    const newYear = maxYear + 1;
    setYearlyVacationDefaults({
      ...yearlyVacationDefaults,
      [newYear]: vacationSettingsEmployee?.vacationDays ?? 20
    });
    setAdditionalVacationDays({
      ...additionalVacationDays,
      [newYear]: 0
    });
  };

  const filteredEmployees = selectedDepartment === 'all'
    ? employees
    : employees.filter(emp => emp.department === selectedDepartment);

  // Sortiranje zaposlenika po odjelu i prezimenu
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    // Prvo sortiranje po odjelu
    const deptA = departments.find(d => d.id === a.department)?.name || '';
    const deptB = departments.find(d => d.id === b.department)?.name || '';
    
    if (deptA !== deptB) {
      return deptA.localeCompare(deptB);
    }
    
    // Ako su u istom odjelu, sortiraj po prezimenu
    return a.lastName.localeCompare(b.lastName);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Učitavanje...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Kontrole */}
      <div className="controls">
        <div className="flex justify-between items-center mb-4">
          <div className="action-buttons flex items-center gap-4">
            <div className="year-selector">
              <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Godina:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="action-button add-employee"
            >
              Dodaj zaposlenika
            </button>
            <button
              onClick={() => setIsAddDepartmentModalOpen(true)}
              className="action-button add-department"
            >
              Dodaj odjel
            </button>
          </div>
          <div className="filter-buttons">
            <button
              className={`filter-button ${selectedDepartment === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedDepartment('all')}
            >
              Svi odjeli
            </button>
            {departments.map(dept => (
              <div key={dept.id} className="flex items-center gap-2">
                <button
                  className={`filter-button ${selectedDepartment === dept.id ? 'active' : ''}`}
                  onClick={() => setSelectedDepartment(dept.id)}
                >
                  {dept.name}
                </button>
                <button
                  onClick={() => handleDeleteDepartment(dept.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Grid zaposlenika */}
      <div className="employee-grid">
        {sortedEmployees.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center py-8">
            Nema zaposlenika za prikaz
          </p>
        ) : (
          sortedEmployees.map(employee => (
            <div key={employee.id} className="employee-card">
              <img
                src={employee.imageUrl || defaultAvatar}
                alt={`${employee.firstName} ${employee.lastName}`}
                className="employee-image"
              />
              <div className="employee-info">
                <h3 className="employee-name">
                  {employee.firstName} {employee.lastName}
                </h3>
                <p className="employee-department">
                  {departments.find(d => d.id === employee.department)?.name || 'Bez odjela'}
                </p>
                <div className="employee-stats">
                  <div className="stat vacation">
                    <span>Godišnji ({selectedYear}):</span>
                    <span>{employee.yearlyCounters?.[selectedYear]?.vacationDaysLeft ?? employee.vacationDays}</span>
                  </div>
                  <div className="stat sick">
                    <span>Bolovanje ({selectedYear}):</span>
                    <span>{employee.yearlyCounters?.[selectedYear]?.sickDays ?? 0}</span>
                  </div>
                  <div className="stat work-from-home">
                    <span>Rad od kuće ({selectedYear}):</span>
                    <span>{employee.yearlyCounters?.[selectedYear]?.workFromHomeDays ?? 0}</span>
                  </div>
                </div>
                <div className="default-stats">
                  <div className="stat default-vacation">
                    <span>Default godišnji ({selectedYear}):</span>
                    <span>{employee.yearlyCounters?.[selectedYear]?.defaultVacationDays ?? employee.vacationDays}</span>
                  </div>
                </div>
                <div className="employee-actions">
                  <button
                    onClick={() => {
                      setEditingEmployee(employee);
                      setIsEditModalOpen(true);
                    }}
                    className="edit-button"
                  >
                    Uredi
                  </button>
                  <button
                    onClick={() => handleOpenVacationSettings(employee)}
                    className="vacation-settings-button"
                  >
                    Postavi godišnji
                  </button>
                  <button
                    onClick={() => handleDeleteEmployee(employee.id)}
                    className="delete-button"
                  >
                    Obriši
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal za dodavanje zaposlenika */}
      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Dodaj novog zaposlenika</h3>
            <form onSubmit={handleAddEmployee}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ime
                </label>
                <input
                  type="text"
                  required
                  value={newEmployee.firstName}
                  onChange={e => setNewEmployee(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Unesite ime"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prezime
                </label>
                <input
                  type="text"
                  required
                  value={newEmployee.lastName}
                  onChange={e => setNewEmployee(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Unesite prezime"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Odjel
                </label>
                <select
                  required
                  value={newEmployee.department}
                  onChange={e => setNewEmployee(prev => ({ ...prev, department: e.target.value }))}
                >
                  <option value="">Odaberite odjel</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL slike
                </label>
                <input
                  type="url"
                  value={newEmployee.imageUrl}
                  onChange={e => setNewEmployee(prev => ({ ...prev, imageUrl: e.target.value }))}
                  placeholder="URL profilne slike (opcionalno)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Broj dana godišnjeg
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={newEmployee.vacationDays}
                  onChange={e => setNewEmployee(prev => ({ ...prev, vacationDays: parseInt(e.target.value) }))}
                />
              </div>

              <div className="buttons">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="cancel-btn"
                >
                  Odustani
                </button>
                <button type="submit" className="save-btn">
                  Spremi
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal za dodavanje odjela */}
      {isAddDepartmentModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Dodaj novi odjel</h3>
            <form onSubmit={handleAddDepartment}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naziv odjela
                </label>
                <input
                  type="text"
                  required
                  value={newDepartment}
                  onChange={e => setNewDepartment(e.target.value)}
                  placeholder="Unesite naziv odjela"
                />
              </div>
              
              <div className="buttons">
                <button
                  type="button"
                  onClick={() => setIsAddDepartmentModalOpen(false)}
                  className="cancel-btn"
                >
                  Odustani
                </button>
                <button type="submit" className="save-btn">
                  Spremi
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal za uređivanje zaposlenika */}
      {isEditModalOpen && editingEmployee && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Uredi zaposlenika</h3>
            <form onSubmit={handleEditEmployee}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ime
                </label>
                <input
                  type="text"
                  required
                  value={editingEmployee.firstName}
                  onChange={e => setEditingEmployee(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="Unesite ime"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prezime
                </label>
                <input
                  type="text"
                  required
                  value={editingEmployee.lastName}
                  onChange={e => setEditingEmployee(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Unesite prezime"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Odjel
                </label>
                <select
                  required
                  value={editingEmployee.department}
                  onChange={e => setEditingEmployee(prev => ({ ...prev, department: e.target.value }))}
                >
                  <option value="">Odaberite odjel</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL slike
                </label>
                <input
                  type="url"
                  value={editingEmployee.imageUrl || ''}
                  onChange={e => setEditingEmployee(prev => ({ ...prev, imageUrl: e.target.value }))}
                  placeholder="URL profilne slike (opcionalno)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Broj dana godišnjeg
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={editingEmployee.vacationDays}
                  onChange={e => setEditingEmployee(prev => ({ ...prev, vacationDays: parseInt(e.target.value) }))}
                />
              </div>

              <div className="buttons">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingEmployee(null);
                  }}
                  className="cancel-btn"
                >
                  Odustani
                </button>
                <button type="submit" className="save-btn">
                  Spremi promjene
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal za postavljanje defaultnog godišnjeg po godinama */}
      {isVacationSettingsModalOpen && vacationSettingsEmployee && (
        <div className="modal-overlay">
          <div className="modal-content vacation-settings-modal">
            <h3>Postavi defaultni godišnji odmor po godinama</h3>
            <p className="employee-name-modal">{vacationSettingsEmployee.firstName} {vacationSettingsEmployee.lastName}</p>
            
            <div className="vacation-settings-table">
              <div className="table-header">
                <div className="table-cell">Godina</div>
                <div className="table-cell">Default godišnji (dana)</div>
                <div className="table-cell">Dodaj godišnji (dana)</div>
                <div className="table-cell">Trenutno dostupno</div>
              </div>
              {Object.entries(yearlyVacationDefaults)
                .sort(([a], [b]) => parseInt(b) - parseInt(a))
                .map(([year, days]) => {
                  const yearCounter = vacationSettingsEmployee?.yearlyCounters?.[year];
                  const currentAvailable = yearCounter?.vacationDaysLeft ?? days;
                  const additional = additionalVacationDays[year] || 0;
                  
                  return (
                    <div key={year} className="table-row">
                      <div className="table-cell year-cell">{year}</div>
                      <div className="table-cell">
                        <input
                          type="number"
                          min="0"
                          value={days}
                          onChange={(e) => {
                            setYearlyVacationDefaults({
                              ...yearlyVacationDefaults,
                              [year]: parseInt(e.target.value) || 0
                            });
                          }}
                          className="vacation-input"
                        />
                      </div>
                      <div className="table-cell">
                        <input
                          type="number"
                          min="0"
                          value={additional}
                          onChange={(e) => {
                            setAdditionalVacationDays({
                              ...additionalVacationDays,
                              [year]: parseInt(e.target.value) || 0
                            });
                          }}
                          className="vacation-input"
                          placeholder="0"
                        />
                      </div>
                      <div className="table-cell current-available">
                        <span className="current-value">{currentAvailable}</span>
                        {additional > 0 && (
                          <span className="additional-preview"> → {currentAvailable + additional}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={handleAddYear}
                className="add-year-button"
              >
                + Dodaj godinu
              </button>
              <div className="action-buttons-right">
                <button
                  type="button"
                  onClick={() => {
                    setIsVacationSettingsModalOpen(false);
                    setVacationSettingsEmployee(null);
                    setYearlyVacationDefaults({});
                    setAdditionalVacationDays({});
                  }}
                  className="cancel-btn"
                >
                  Odustani
                </button>
                <button
                  type="button"
                  onClick={handleSaveVacationSettings}
                  className="save-btn"
                >
                  Spremi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;